import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table } from "antd";
import { SearchOutlined, ReloadOutlined, TrophyOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import "./leaderBoardV2.css";

const apiUrl = process.env.REACT_APP_API_TesterStatistics;

const LeaderBoardV2 = () => {
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState([]);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const formattedDate = yesterday.toLocaleDateString("en-GB");

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch leaderboard data");
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : data?.body || data?.data || []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="leaderboard-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="leaderboard-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "leaderboard-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) => {
      const val = record[dataIndex];
      if (val == null) return false;
      return val.toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a.valid_otps ?? a.otps_count ?? 0;
    const bVal = b.valid_otps ?? b.otps_count ?? 0;
    return bVal - aVal;
  });

  const getRankClass = (index) => {
    if (index === 0) return "leaderboard-v2-rank leaderboard-v2-rank-gold";
    if (index === 1) return "leaderboard-v2-rank leaderboard-v2-rank-silver";
    if (index === 2) return "leaderboard-v2-rank leaderboard-v2-rank-bronze";
    return "leaderboard-v2-rank";
  };

  const columns = [
    {
      title: "#",
      key: "ranking",
      width: "10%",
      render: (_, __, index) => (
        <span className={`leaderboard-v2-rank-badge ${getRankClass(index)}`}>
          {index + 1}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "35%",
      ...getColumnSearchProps("first_name"),
      onFilter: (value, record) => {
        const fullName = `${record.first_name || ""} ${record.last_name || ""}`.toLowerCase();
        return fullName.includes(value.toLowerCase());
      },
      render: (text, user) => {
        const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        return searchedColumn === "first_name" && searchText ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={name}
          />
        ) : (
          name
        );
      },
      sorter: (a, b) =>
        `${a.first_name || ""} ${a.last_name || ""}`.localeCompare(
          `${b.first_name || ""} ${b.last_name || ""}`
        ),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "OTPs",
      dataIndex: "valid_otps",
      key: "valid_otps",
      width: "22%",
      render: (val, record) => {
        const count = val ?? record.otps_count ?? "-";
        return <span className="leaderboard-v2-otp-value">{count}</span>;
      },
      ...getColumnSearchProps("valid_otps"),
      sorter: (a, b) => (a.valid_otps ?? a.otps_count ?? 0) - (b.valid_otps ?? b.otps_count ?? 0),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
  ];

  return (
    <div className="leaderboard-v2">
      <div className="leaderboard-v2-page-header">
        <div className="leaderboard-v2-header-content">
          <div className="leaderboard-v2-header-icon">
            <TrophyOutlined />
          </div>
          <div>
            <h1 className="leaderboard-v2-page-title">Leaderboard</h1>
            <p className="leaderboard-v2-page-subtitle">
              Top testers by OTPs generated · Stats till {formattedDate}
            </p>
          </div>
        </div>
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={fetchData}
          loading={loading}
          className="leaderboard-v2-refresh-btn"
        >
          Refresh
        </Button>
      </div>

      {sortedUsers.length > 0 && (
        <div className="leaderboard-v2-stats-bar">
          <span className="leaderboard-v2-stats-count">
            {sortedUsers.length} testers competing
          </span>
          <span className="leaderboard-v2-stats-top">
            Top score: {(sortedUsers[0]?.valid_otps ?? sortedUsers[0]?.otps_count ?? 0).toLocaleString()} OTPs
          </span>
        </div>
      )}

      <div className="leaderboard-v2-card">
        <Table
          columns={columns}
          dataSource={sortedUsers}
          loading={loading}
          rowKey={(r, i) => r.id ?? r.user_id ?? `row-${i}`}
          locale={{ emptyText: loading ? "Loading..." : "No data yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="leaderboard-v2-table"
        />
      </div>
    </div>
  );
};

export default LeaderBoardV2;
